import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddVoiceoverSceneTool } from 'js/src/agent/tools/AddVoiceoverSceneTool';
import { SetCaptionsTool } from 'js/src/agent/tools/SetCaptionsTool';

export const createFaqVideoSkill: Skill = {
    name: SKILL_NAMES.createFaqVideo,
    description:
        'FAQ/Q&A-style video — questions answered one at a time (myth-busting, "did you know", rapid-fire Q&A).',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'FAQ video',
        blurb: 'Answer common questions one at a time.',
        icon: 'quiz',
        gradient: ['#82679B', '#6D71A0'],
    },
    get instructions(): string {
        return `# Create FAQ Video

The Q&A reveal format: questions answered one at a time. An AI voiceover narrates
the questions and answers; on-screen text reinforces each reveal.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill adds the
Q&A formula, and its rules win where they conflict.

## GOLDEN RULE — question and answer never visible at the same time
The viewer must never see the answer before the voiceover starts saying it. Build
each pair as separate overlays (subscenes don't exist on a voice-driven video):
- ${AddVoiceoverSceneTool.toolName} for the question's words, then separately for the answer's
  words — each overlay anchors to its own word range, so the reveal timing comes
  from the audio automatically.
- Never let one overlay's word range span both a question and its answer.
- A long question or answer can split across overlays at natural clause breaks —
  every overlay on the question side keeps the question treatment, every overlay
  on the answer side the answer treatment.

## Question vs. answer split
- The question overlay is text-only — a clean, large-text card, never media.
  Only pick designs with no media asset. Phrase it as an actual question, with a
  question mark.
- The answer overlay always carries media that concretely illustrates it — only
  pick designs with a media slot (a text-only listicle/stat design is for
  questions, never answers). Don't restate the question in the answer text.
- "title" category designs are reserved for the opening hook and chapter breaks —
  don't spend one on a regular question.
- Never a caption-style design for either side.
- Vary the designs across pairs — separately for questions and for answers. The
  built-in media/no-media alternation is the video's rhythm.

## Script structure
- **The narration itself is the questions and the answers.** Every pair is a
  spoken question followed by its spoken answer — "How is coffee harvested?
  Farmers hand-pick the ripe cherries...", never "First, farmers pick...". If the
  voiceover reads as a list of steps or facts with Q/A labels only in the
  on-screen text, it's not an FAQ build.
- Use the user's Q&A list as-is when given; otherwise draft one.
- Open with a hook line naming the topic (it becomes the title card, on a "title"
  design); write question-then-answer pairs back to back; close with a real outro —
  a brief recap plus a topic-specific follow/CTA line on an "outro" design.
- Group questions by topic by writing the transition into the narration itself
  ("Now, about pricing...") covered by a chapter-title overlay — don't break the
  single voiceover scene group.
- **Captions are OFF for this format** — on-screen text does that job. Call
  ${SetCaptionsTool.toolName} with enabled: false right after generating the voiceover.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- Confirm no overlay shows a question and its answer at once
- Confirm no question media telegraphs the answer (too early!).
- Tally overlay by overlay: every question overlay text-only, every answer overlay
  with visible media — a text-only answer is the bug to hunt.
- Confirm that both design rotations actually vary.`;
    },
};
